import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Film, Award, Globe, Sparkles, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS, STATS, CURRENT_FILMS } from '@/lib/index';
import Layout from '@/components/Layout';
import { useEffect, useState } from 'react';
interface AnimatedStatProps {
  end: number;
  label: string;
  suffix?: string;
}
function AnimatedStat({
  end,
  label,
  suffix = ''
}: AnimatedStatProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end]);
  return <div className="text-center">
      <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-accent mb-1 md:mb-2">
        {count}{suffix}
      </div>
      <div className="text-xs sm:text-sm md:text-base text-muted-foreground">{label}</div>
    </div>;
}
export default function Home() {
  const _chaosFilm = CURRENT_FILMS[0];
  return <Layout>
      <div className="min-h-screen">

        {/* HERO — full-width cinematic GIF background */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={IMAGES.HERO_INTRO_GIF} alt="Keshhav Panneriy Cinematic Intro" className="w-full h-full object-cover" style={{
            opacity: 0.82
          }} />
            <div className="absolute inset-0 bg-gradient-to-b from-background/35 via-background/10 to-background/55" />
          </div>
          {/* Hero text overlay — visible on all screens */}
          <div className="relative z-10 text-center px-4">
            
            
          </div>
        </section>

        {/* PORTRAIT + ARCHITECT TEXT SECTION */}
        <section className="py-12 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Portrait image */}
              <div className="relative w-full rounded-xl overflow-hidden" style={{
              boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 50px rgba(245,200,66,0.12)'
            }}>
                <img src={IMAGES.KESHHAV_DIRECTOR_13_1_13} alt="Keshhav Panneriy – Director" className="w-full h-auto object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
              </div>

              {/* Integrated text block */}
              <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="space-y-4 md:space-y-6">
                <div className="h-px w-20 bg-gradient-to-r from-accent to-transparent" />

                <p className="text-sm sm:text-base md:text-xl text-foreground/90 font-light leading-relaxed text-center">
                  Visionary Film Director | Writer | Conceptualizer | Media Leader | Content Business Strategist
                </p>

                <h2 className="font-cinzel text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-accent leading-tight text-center">
                  THE ARCHITECT OF<br />TOMORROW'S CINEMA
                </h2>

                <p className="text-sm sm:text-base md:text-lg font-light leading-relaxed text-justify text-foreground/90">
                  A filmmaker who blends emotion, structure, and scale into narratives that transcend borders.
                </p>

                <div className="h-px w-full bg-gradient-to-r from-accent/40 via-accent/20 to-transparent" />

                <p className="text-sm sm:text-base md:text-base leading-[1.9] text-justify text-foreground/80">
                  With an MFA from Toronto Film School and over 20 years of experience spanning Mumbai, Hollywood, and global markets, Keshhav Panneriy operates at the intersection of commercial viability and artistic excellence.
                </p>

                <p className="text-sm sm:text-base text-accent font-semibold italic leading-relaxed border-l-2 border-accent pl-4">
                  Not just a director. A cinematic architect engineering the future of storytelling.
                </p>

                <p className="text-sm sm:text-base md:text-lg italic leading-relaxed text-center text-foreground/80">
                  Bridging the Soul of Indian Cinema with the Rigor of Hollywood
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Link to={ROUTE_PATHS.FILMOGRAPHY} className="w-full sm:w-auto">
                    <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 text-sm md:text-base">
                      EXPLORE FILMOGRAPHY
                    </Button>
                  </Link>
                  <Link to={ROUTE_PATHS.BEHIND_THE_SCENES} className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-accent text-accent hover:bg-accent/10 text-sm md:text-base">
                      BEHIND THE SCENES
                    </Button>
                  </Link>
                </div>

                <div className="h-px w-20 bg-gradient-to-r from-accent to-transparent" />
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* STATS BAR */}
        <section className="py-10 md:py-16 bg-card border-y border-border">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <AnimatedStat end={20} label="Years Artistry" suffix="+" />
              <AnimatedStat end={15} label="Years Filmmaking" suffix="+" />
              <AnimatedStat end={110} label="International Awards" suffix="+" />
              <AnimatedStat end={6} label="Countries" />
            </div>
          </div>
        </section>

        {/* 4 CREATIVE PILLARS */}
        <section className="py-14 md:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-10 md:mb-16">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3 md:mb-4">
                4 Creative Pillars
              </h2>
              <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
                The Foundation of Cinematic Excellence
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[{
              icon: Film,
              title: 'Character-Centric Commercialism',
              description: 'Crafting commercially viable narratives anchored in authentic, deeply human characters that resonate across cultures.'
            }, {
              icon: Sparkles,
              title: 'Visual & Aesthetic Identity',
              description: 'Creating distinctive visual signatures that elevate storytelling through meticulous cinematography and production design.'
            }, {
              icon: Award,
              title: 'Architectural Narrative Layering',
              description: 'Building multi-dimensional stories with intricate plot structures that reward repeated viewing and deep analysis.'
            }, {
              icon: Globe,
              title: 'Strategic Genre Synthesis',
              description: 'Blending genres with precision to create unique cinematic experiences that transcend traditional boundaries.'
            }].map((pillar, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }}>
                  <Card className="h-full bg-card hover:bg-card/80 transition-colors border-border shadow-cinematic">
                    <CardContent className="p-5 md:p-6 space-y-3 md:space-y-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/10 flex items-center justify-center">
                        <pillar.icon className="w-5 h-5 md:w-6 md:h-6 text-accent" />
                      </div>
                      <h3 className="font-display text-base md:text-xl font-semibold text-foreground">
                        {pillar.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {pillar.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* AUTEUR PARALLELS */}
        <section className="py-14 md:py-24 bg-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-10 md:mb-16">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3 md:mb-4">
                In Company of Cinematic Titans
              </h2>
              <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Drawing inspiration from the masters while forging a unique path
              </p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {[{
              name: 'Francis Ford Coppola',
              parallel: 'Architectural Narrative Layering',
              description: 'Epic storytelling with intricate plot structures'
            }, {
              name: 'Ridley Scott',
              parallel: 'Visual & Aesthetic Identity',
              description: 'Distinctive visual signatures and world-building'
            }, {
              name: 'George Lucas',
              parallel: 'Universe Building',
              description: 'Creating expansive cinematic universes'
            }, {
              name: 'James Cameron',
              parallel: 'Technology Pioneer',
              description: 'Pushing technical boundaries in filmmaking'
            }, {
              name: 'Peter Jackson',
              parallel: 'Epic Scale',
              description: 'Ambitious multi-film narrative arcs'
            }].map((auteur, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }}>
                  <Card className="h-full bg-background hover:bg-background/80 transition-colors border-accent/20">
                    <CardContent className="p-4 md:p-6 space-y-2 md:space-y-3 text-center">
                      <h3 className="font-display text-sm md:text-lg font-semibold text-accent">
                        {auteur.name}
                      </h3>
                      <p className="text-xs md:text-sm font-medium text-foreground">
                        {auteur.parallel}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {auteur.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* TECHNOLOGY VISION */}
        <section className="py-14 md:py-24 bg-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-10 md:mb-16">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3 md:mb-4">
                Technology Vision
              </h2>
              <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Engineering the Future of Cinema
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[{
              title: 'AI Integration',
              description: 'Leveraging artificial intelligence for enhanced storytelling, pre-visualization, and post-production workflows.'
            }, {
              title: 'Virtual Production',
              description: 'Pioneering LED volume stages and real-time rendering for immersive filmmaking experiences.'
            }, {
              title: 'Immersive Technologies',
              description: 'Exploring VR, AR, and stereoscopic 3D to create groundbreaking cinematic experiences.'
            }, {
              title: 'Global Narratives',
              description: 'Crafting stories that transcend borders through universal themes and cutting-edge distribution.'
            }].map((tech, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }}>
                  <Card className="h-full bg-background hover:bg-background/80 transition-colors border-border">
                    <CardContent className="p-5 md:p-6 space-y-3">
                      <h3 className="font-display text-base md:text-xl font-semibold text-accent">
                        {tech.title}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {tech.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* WATCH NOW — Streaming Section */}
        <section className="py-12 md:py-20 bg-secondary/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-8 md:mb-12">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">
                WATCH ON STREAMING
              </h2>
              <div className="h-1 w-24 bg-accent mx-auto mb-4 md:mb-6" />
              <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto">
                Experience Keshhav Panneriy's award-winning films now available on major streaming platforms
              </p>
            </motion.div>

            <div className="flex flex-col gap-4 md:gap-6 justify-center items-center max-w-3xl mx-auto">
              {/* Amazon Prime Card */}
              <motion.a href="https://www.primevideo.com/detail/0HG49KPOU9ZBC5F42F3GH04QEW/ref=atv_dp_share_cu_r" target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group flex items-center gap-4 md:gap-5 bg-card border-2 border-border hover:border-accent/60 rounded-xl p-5 md:p-6 w-full shadow-cinematic hover:shadow-gold transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#00A8E0]/10 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8 fill-[#00A8E0]" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.127 0c5.283 0 9.017 3.565 9.017 8.682 0 4.968-3.57 8.453-8.617 8.453-2.062 0-3.896-.696-5.23-1.96l-1.24 4.825H3.32L7.31 4.73C8.617 1.71 10.713 0 13.127 0zm-.126 3.04c-1.91 0-3.202 1.233-3.83 3.52l-.988 3.832c.348 1.433 1.483 2.35 2.93 2.35 2.212 0 3.658-1.71 3.658-4.326 0-3.178-1.25-5.376-1.77-5.376z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Stream Now On</div>
                  <div className="font-display text-lg md:text-xl font-bold text-foreground group-hover:text-accent transition-colors">Amazon Prime Video</div>
                  <div className="text-xs md:text-sm text-accent mt-1">Watch Keshhav Panneriy Films →</div>
                </div>
              </motion.a>

              {/* YouTube Card */}
              <motion.a href="https://youtu.be/yArir1D9lzU?si=BblmCU4Q3IyrUGvm" target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group relative overflow-hidden flex items-center gap-4 md:gap-5 border-2 border-border hover:border-accent/60 rounded-xl p-5 md:p-6 w-full shadow-cinematic hover:shadow-gold transition-all duration-300">
                <img src={IMAGES.JEENA_MOVIE_BANNER} alt="Jeena Isi Ka Naam Hai" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/60 to-background/40" />
                <div className="relative z-10 flex items-center gap-4 md:gap-5">
                  <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-red-600/20 border border-red-500/40 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8 fill-red-500" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 uppercase tracking-wider mb-1">Watch On</div>
                    <div className="font-display text-lg md:text-xl font-bold text-white group-hover:text-accent transition-colors">YouTube</div>
                    <div className="text-xs md:text-sm text-accent font-semibold mt-1">▶ Watch Full Movie →</div>
                  </div>
                </div>
              </motion.a>
            </div>
          </div>
        </section>

        {/* LET'S CONNECT */}
        <section className="py-14 md:py-24 bg-gradient-to-b from-background to-card">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="max-w-3xl mx-auto text-center space-y-5 md:space-y-8">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-accent">
                LET'S CREATE TOGETHER
              </h2>
              <p className="text-sm md:text-base lg:text-xl text-muted-foreground">
                Ready to bring your vision to life? Connect with a filmmaker who bridges artistic excellence with commercial success.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
                <a href="mailto:info@keshhavpanneriy.com" className="flex items-center gap-2 text-foreground hover:text-accent transition-colors">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base lg:text-lg break-all">info@keshhavpanneriy.com</span>
                </a>
                <span className="hidden sm:inline text-muted-foreground">|</span>
                <a href="tel:+919833447935" className="flex items-center gap-2 text-foreground hover:text-accent transition-colors">
                  <Phone className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base lg:text-lg">+91 98334 47935</span>
                </a>
              </div>
              <div className="pt-2 md:pt-4">
                <Link to={ROUTE_PATHS.ABOUT}>
                  <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-gold text-sm md:text-base">
                    LEARN MORE ABOUT KESHHAV
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </Layout>;
}